// src/app/shared/organization-switcher/organization-switcher.component.ts
import { AsyncPipe } from '@angular/common';
import { Component, EventEmitter, Input, Output, inject } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';

import { AuthService, User } from '../auth/auth.service';
import { OrganizationMembership } from '../auth/membership.model';
import { RoleDisplayNamePipe } from '../role-display-name.pipe';
import { NavigationService } from '../auth/navigation.service';

/**
 * Shows the hospital the user is currently working in and lets them switch.
 * Renders NOTHING unless the user belongs to more than one hospital.
 */
@Component({
  selector: 'app-organization-switcher',
  imports: [AsyncPipe, MatMenuModule, MatIconModule, RoleDisplayNamePipe],
  template: `
    @if (auth.user | async; as user) {
      @if (memberships(user).length > 1) {
        <div class="org-switcher" [class.collapsed]="collapsed">
          <button
            type="button"
            class="org-btn"
            [matMenuTriggerFor]="orgMenu"
            [disabled]="switching"
            [title]="'Current hospital: ' + activeName(user)"
            aria-label="Switch hospital">
            <mat-icon class="org-icon">{{ switching ? 'sync' : 'domain' }}</mat-icon>
            @if (!collapsed) {
              <span class="org-text">
                <span class="org-label">Current hospital</span>
                <span class="org-name">{{ activeName(user) }}</span>
              </span>
              <mat-icon class="org-chevron">unfold_more</mat-icon>
            }
          </button>

          @if (error) {
            <div class="org-error" role="alert">{{ error }}</div>
          }

          <mat-menu #orgMenu="matMenu">
            @for (m of memberships(user); track m.organizationId) {
              <button mat-menu-item (click)="switchTo(m)" [disabled]="m.isActive">
                <mat-icon>{{ m.isActive ? 'radio_button_checked' : 'radio_button_unchecked' }}</mat-icon>
                <span class="org-item">
                  <span class="org-item-name">{{ m.organizationName || 'Hospital' }}</span>
                  <span class="org-item-roles" [title]="allRoles(m)">
  @for (r of visibleRoles(m); track r) {
    <span class="role-chip">{{ r | roleDisplayName }}</span>
  }
  @if (extraRoleCount(m) > 0) {
    <span class="role-chip more">+{{ extraRoleCount(m) }}</span>
  }
</span>
                </span>
              </button>
            }
          </mat-menu>
        </div>
      }
    }
  `,
  styles: [`
    .org-switcher {
      padding: 10px 13px 0;
      background-color: var(--mat-sys-surface-container-low);
    }
    .org-switcher.collapsed { padding: 10px 0 0; text-align: center; }

    .org-btn {
      display: flex;
      align-items: center;
      gap: 10px;
      width: 100%;
      padding: 8px 10px;
      border: 1px solid var(--mat-sys-outline-variant);
      border-radius: 12px;
      background: var(--mat-sys-surface-container);
      color: var(--mat-sys-on-surface);
      cursor: pointer;
      text-align: left;
      font: inherit;
    }
    .role-chip.more {
        background: var(--mat-sys-surface-container-highest);
        color: var(--mat-sys-on-surface-variant);
      }
    .org-btn:hover:not(:disabled) { background: var(--mat-sys-surface-container-high); }
    .org-btn:disabled { opacity: 0.7; cursor: progress; }
    .collapsed .org-btn { width: auto; padding: 8px; justify-content: center; }

    .org-icon { color: var(--mat-sys-primary); flex-shrink: 0; }
    .org-text { display: flex; flex-direction: column; min-width: 0; flex: 1; }
    .org-label { font-size: 11px; color: var(--mat-sys-on-surface-variant); line-height: 1.2; }
    .org-name {
      font-weight: 600;
      font-size: 14px;
      line-height: 1.3;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }
    .org-chevron { flex-shrink: 0; color: var(--mat-sys-on-surface-variant); }

    .org-error { margin-top: 6px; font-size: 12px; color: var(--mat-sys-error); }

    .org-item { display: flex; flex-direction: column; line-height: 1.3; }
    .org-item-name { font-weight: 500; }
    .org-item-roles { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 2px; }
    .role-chip {
      font-size: 11px;
      padding: 0 6px;
      border-radius: 8px;
      background: var(--mat-sys-secondary-container);
      color: var(--mat-sys-on-secondary-container);
    }
  `]
})
export class OrganizationSwitcherComponent {
  readonly auth = inject(AuthService);
  private navigationService = inject(NavigationService);

  /** Pass the wrapper's collapsed state so only the icon shows when the sidenav is collapsed. */
  @Input() collapsed = false;

  /** Fires after a successful switch. The parent decides what to do (navigate, reload...). */
  @Output() switched = new EventEmitter<void>();

  switching = false;
  error: string | null = null;
private readonly maxRoleChips = 3;

visibleRoles(m: OrganizationMembership): string[] {
  return (m.roles ?? []).slice(0, this.maxRoleChips);
}

extraRoleCount(m: OrganizationMembership): number {
  return Math.max(0, (m.roles?.length ?? 0) - this.maxRoleChips);
}

/** Tooltip with the full list, so nothing is hidden from the user */
allRoles(m: OrganizationMembership): string {
  return (m.roles ?? []).join(', ');
}
  memberships(user: User | null): OrganizationMembership[] {
    return user?.memberships ?? [];
  }

  activeName(user: User | null): string {
    const active = this.memberships(user).find(m => m.isActive);
    return active?.organizationName || user?.organizationName || 'Current hospital';
  }

  switchTo(target: OrganizationMembership): void {
    if (target.isActive || this.switching) {
      return;
    }

    this.switching = true;
    this.error = null;

    this.auth.switchOrganization(target.organizationId).subscribe(result => {
      this.switching = false;

      if (result.ok) {
        this.switched.emit();
      } else {
        this.error = result.message ?? 'Could not switch hospital.';
        setTimeout(() => (this.error = null), 6000);
      }
    });
  }
}